import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import {
  EMPTY_FIRM_CONTACT_VALIDATION,
} from "../../utils/firmContactUtils";
import {
  getAccountingOfficeDetail,
  getAccountingOfficeGroup,
  officeDisplayName,
  type AccountingOfficeDetail,
  type AccountingOfficeGroupTab,
} from "../../utils/accountingOfficeDetail";
import { resolveAccountingOfficeContacts } from "../../utils/accountingContactInheritance";
import AccountingFirmOverview from "./AccountingFirmOverview";
import AccountingInvoiceHistory from "./AccountingInvoiceHistory";
import AccountingPaymentSummary from "./AccountingPaymentSummary";
import AccountingOfficeGroupTabs from "./AccountingOfficeGroupTabs";
import EditAccountingParametersModal from "./EditAccountingParametersModal";
import { isAccountingTab, type AccountingTab } from "./accountingTabs";

export default function AccountingFirmDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();

  const backTab = useMemo((): AccountingTab => {
    const fromTab = (location.state as { fromTab?: unknown } | null)?.fromTab;
    return isAccountingTab(fromTab) ? fromTab : "accountingAll";
  }, [location.state]);

  const [office, setOffice] = useState<AccountingOfficeDetail | null>(null);
  const [contactValidation, setContactValidation] = useState(
    EMPTY_FIRM_CONTACT_VALIDATION,
  );
  const [inheritContactsNotice, setInheritContactsNotice] = useState<
    string | null
  >(null);
  const [ccContactDisplay, setCcContactDisplay] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupTabs, setGroupTabs] = useState<AccountingOfficeGroupTab[]>([]);
  const [editParametersOpen, setEditParametersOpen] = useState(false);
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);
  const bumpBillingRefresh = useCallback(() => {
    setBillingRefreshKey((k) => k + 1);
  }, []);

  const loadOffice = useCallback(async (officeId: string) => {
    setLoading(true);
    setError(null);
    try {
      const officeData = await getAccountingOfficeDetail(officeId);

      if (!officeData) {
        setError("Office not found.");
        setOffice(null);
        return;
      }

      const contactResolution = await resolveAccountingOfficeContacts(
        officeData,
      ).catch(() => null);

      setOffice(officeData);
      setContactValidation(
        contactResolution?.validation ?? EMPTY_FIRM_CONTACT_VALIDATION,
      );
      setInheritContactsNotice(contactResolution?.inheritNotice ?? null);
      setCcContactDisplay(contactResolution?.ccContactDisplay ?? "");
    } catch {
      setError("Unable to load office details.");
      setOffice(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) {
      setError("Invalid office id.");
      setLoading(false);
      return;
    }
    void loadOffice(id);
  }, [id, loadOffice]);

  useEffect(() => {
    if (!id) {
      setGroupTabs([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const { tabs } = await getAccountingOfficeGroup(id);
        if (!cancelled) {
          setGroupTabs(tabs);
        }
      } catch {
        if (!cancelled) {
          setGroupTabs([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSelectOfficeTab = useCallback(
    (officeId: string) => {
      if (!officeId || officeId === id) {
        return;
      }
      navigate(`/accounting/${officeId}`, { state: location.state });
    },
    [id, location.state, navigate],
  );

  useEffect(() => {
    if (!id || !office) {
      return;
    }

    const title = officeDisplayName(office);
    setPageMeta({
      title,
      breadcrumbLabels: {
        "/accounting": "Accounting",
        [`/accounting/${id}`]: title,
      },
    });

    return () => {
      clearPageMeta();
    };
  }, [clearPageMeta, id, office, setPageMeta]);

  const content = loading ? (
    <div className="flex flex-col items-center justify-center py-16 text-gray-font">
      Loading office details...
    </div>
  ) : error || !office ? (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <p className="text-gray-font">{error ?? "Office not found."}</p>
      <Link
        to="/accounting"
        state={{ activeTab: backTab }}
        className="text-[#0070CD] hover:underline"
      >
        Back to Accounting
      </Link>
    </div>
  ) : (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        <AccountingFirmOverview
          office={office}
          contactValidation={contactValidation}
          inheritContactsNotice={inheritContactsNotice}
          ccContactDisplay={ccContactDisplay}
          onEditParameters={() => setEditParametersOpen(true)}
          onOpenCreditLedger={() =>
            navigate(`/accounting/${id}/balance`, { state: location.state })
          }
        />
        <AccountingPaymentSummary
          office={office}
          refreshKey={billingRefreshKey}
        />
      </div>
      <AccountingInvoiceHistory
        office={office}
        billingRefreshKey={billingRefreshKey}
        onBillingRefresh={bumpBillingRefresh}
      />
      <EditAccountingParametersModal
        office={office}
        isOpen={editParametersOpen}
        onClose={() => setEditParametersOpen(false)}
        onSaved={() => {
          if (id) {
            void loadOffice(id);
          }
        }}
      />
    </>
  );

  const showOfficeTabs = !loading && !error && office != null;

  return (
    <div className="flex min-w-0 flex-col">
      {showOfficeTabs ? (
        <AccountingOfficeGroupTabs
          tabs={groupTabs}
          activeOfficeId={id ?? ""}
          onSelect={handleSelectOfficeTab}
        />
      ) : null}
      <div className="flex flex-col gap-6 bg-white p-6">{content}</div>
    </div>
  );
}
